import { CHECK_STATUS } from '../types';

export const id = 'connectionSpeed';
export const label = 'Connection Speed';

const BYTES_TO_DOWNLOAD = 10174706; // 10 MB test image
const SPEED_TIMEOUT_MS = 30000; // max wait before marking FAIL (Image ignores AbortSignal)
const TIER_1080P_MBPS = 15;
const TIER_REAL_TIME_MBPS = 6;
const TIER_ESSENTIAL_MBPS = 3;

const TIER_LABEL = {
  '1080p': '1080p capable',
  real_time: 'real-time streaming',
  essential_only: 'essential streams only',
  below_essential: 'below minimum',
};

export function run(ctx) {
  if (typeof window === 'undefined' || window.performance == null) {
    return Promise.resolve({
      id,
      label,
      status: CHECK_STATUS.SKIPPED,
      detail: 'Performance API unavailable',
      detailData: {},
      durationMs: 0,
    });
  }

  return new Promise((resolve) => {
    const start = Date.now();
    window.performance.clearResourceTimings();

    const uri = `https://cdn.upshow.tv/10MB.jpg?upbust=${Date.now()}`;
    const download = new Image();
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      download.src = ''; // abort the in-flight load
      resolve({
        id,
        label,
        status: CHECK_STATUS.FAIL,
        detail: 'Speed test timed out',
        detailData: {},
        durationMs: Date.now() - start,
      });
    }, SPEED_TIMEOUT_MS);

    download.onload = function () {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      const durationMs = Date.now() - start;
      const perf = window.performance.getEntriesByName(download.src);

      if (!perf || perf.length === 0) {
        resolve({
          id,
          label,
          status: CHECK_STATUS.SKIPPED,
          detail: 'Performance API unavailable',
          detailData: {},
          durationMs,
        });
        return;
      }

      const totalSeconds = perf[0].duration / 1000;
      if (!isFinite(totalSeconds) || totalSeconds <= 0) {
        resolve({
          id,
          label,
          status: CHECK_STATUS.SKIPPED,
          detail: 'Speed unmeasurable — invalid resource timing',
          detailData: {},
          durationMs,
        });
        return;
      }
      const totalMBits = (BYTES_TO_DOWNLOAD * 8) / 1024 / 1024;
      const measuredMbps = Math.round((totalMBits / totalSeconds) * 10) / 10;

      let status;
      let tier;

      if (measuredMbps >= TIER_1080P_MBPS) {
        status = CHECK_STATUS.PASS;
        tier = '1080p';
      } else if (measuredMbps >= TIER_REAL_TIME_MBPS) {
        status = CHECK_STATUS.PASS;
        tier = 'real_time';
      } else if (measuredMbps >= TIER_ESSENTIAL_MBPS) {
        status = CHECK_STATUS.AT_RISK;
        tier = 'essential_only';
      } else {
        status = CHECK_STATUS.FAIL;
        tier = 'below_essential';
      }

      resolve({
        id,
        label,
        status,
        detail: `${measuredMbps} Mbps — ${TIER_LABEL[tier]}`,
        detailData: { measuredMbps, tier },
        durationMs,
      });
    };

    download.onerror = function (err) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      const durationMs = Date.now() - start;
      resolve({
        id,
        label,
        status: CHECK_STATUS.FAIL,
        detail: `Image download error: ${err?.message || 'unknown'}`,
        detailData: {},
        durationMs,
      });
    };

    download.src = uri;
  });
}
